import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { AcquisitionPoint } from '@/hooks/useSuperStats';

interface AcquisitionChartProps {
  data: AcquisitionPoint[];
  loading: boolean;
}

/**
 * Courbe d'acquisition : nouveaux sites de la plateforme et nouveaux comptes
 * (utilisateurs) enregistrés chaque mois sur les 12 derniers mois.
 */
export default function AcquisitionChart({ data, loading }: AcquisitionChartProps) {
  const totalSites = data.reduce((sum, point) => sum + point.sites, 0);
  const totalAccounts = data.reduce((sum, point) => sum + point.accounts, 0);
  const totalLastYear = data.reduce((sum, point) => sum + point.sitesLastYear, 0);
  const hasData = totalSites > 0 || totalAccounts > 0 || totalLastYear > 0;
  const yoy = totalLastYear > 0
    ? ((totalSites - totalLastYear) / totalLastYear) * 100
    : null;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-5 md:mb-6">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-line-chart-line text-primary-600"></i>
          Acquisition — nouveaux sites &amp; comptes
        </h3>
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="flex items-center gap-1.5 text-foreground-600">
            <span className="w-2.5 h-2.5 rounded-full bg-primary-500"></span>
            {totalSites.toLocaleString('fr-FR')} sites
          </span>
          <span className="flex items-center gap-1.5 text-foreground-600">
            <span className="w-2.5 h-2.5 rounded-full bg-secondary-300"></span>
            {totalLastYear.toLocaleString('fr-FR')} an dernier
          </span>
          <span className="flex items-center gap-1.5 text-foreground-600">
            <span className="w-2.5 h-2.5 rounded-full bg-accent-500"></span>
            {totalAccounts.toLocaleString('fr-FR')} comptes
          </span>
          {yoy != null && (
            <span
              className={`inline-flex items-center gap-0.5 font-semibold rounded-full px-2 py-0.5 ${
                yoy >= 0 ? 'text-accent-700 bg-accent-100' : 'text-red-600 bg-red-50'
              }`}
            >
              <i className={yoy >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
              {yoy >= 0 ? '+' : ''}{yoy.toFixed(1)}% vs an dernier
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-foreground-500 mb-4">
        Évolution mensuelle sur les 12 derniers mois, comparée au même mois de l&apos;année précédente.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <i className="ri-line-chart-line text-3xl text-foreground-300 mb-2"></i>
          <p className="text-sm text-foreground-400">
            Aucune donnée d&apos;acquisition disponible pour le moment.
          </p>
        </div>
      ) : (
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }}
                axisLine={{ stroke: 'oklch(var(--background-200))' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid oklch(var(--background-200))',
                  fontSize: 12,
                }}
                labelStyle={{ color: 'oklch(var(--foreground-700))', fontWeight: 600 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value: string) => (
                  <span className="text-foreground-600">{value}</span>
                )}
              />
              <Bar
                dataKey="sitesLastYear"
                name="Nouveaux sites (an dernier)"
                fill="oklch(var(--secondary-300))"
                radius={[4, 4, 0, 0]}
                maxBarSize={18}
              />
              <Bar
                dataKey="sites"
                name="Nouveaux sites"
                fill="oklch(var(--primary-500))"
                radius={[4, 4, 0, 0]}
                maxBarSize={18}
              />
              <Line
                type="monotone"
                dataKey="accounts"
                name="Nouveaux comptes"
                stroke="oklch(var(--accent-500))"
                strokeWidth={2.5}
                dot={{ r: 3, fill: 'oklch(var(--accent-500))' }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}